import { ForwardedRef, forwardRef, useEffect, useState } from 'react'
import { useDebounce } from 'react-use'
import { Dimensions, Position } from 'types'
import { TextStyle } from '../../../types'
import Layer, { LayerProps } from '../layer'
import { ArtboardLayerRef } from '../types/refs'
import useTextPosition from './hooks/use-text-position'
import useTextSprite from './hooks/use-text-sprite'

const DEFAULT_STYLE: TextStyle = {
    color: '#fff',
    fontFamily: 'interface',
    fontSize: 20,
    fontWeight: 'normal',
}

interface Props extends LayerProps {
    text: string
    textStyle?: TextStyle
}

const ArtboardText = (
    {
        defaultPosition: positionProp,
        disabled,
        text,
        textStyle = DEFAULT_STYLE,
        onPositionUpdate,
        ...layerProps
    }: Props,
    ref: ForwardedRef<ArtboardLayerRef>
) => {
    const [dimensions, setDimensions] = useState<Dimensions>({ height: 0, width: 0 })
    const [currentPosition, setCurrentPosition] = useState<Position | undefined>(positionProp)
    const [draggedPosition, setDraggedPosition] = useState<Position | undefined>(positionProp)
    const defaultPosition = useTextPosition(dimensions, draggedPosition)

    const scale = currentPosition && defaultPosition ? currentPosition.width / defaultPosition.width : 1
    const sprite = useTextSprite(text, textStyle, scale)

    /**
     * When a text layer is dragged, freeze default position to prevent a
     * positioned layer from moving when cropping changes. Text layers
     * that have not been dragged and do not have a position prop will
     * auto-update their position based on crop changes.
     */
    useDebounce(
        () => {
            if (!draggedPosition && JSON.stringify(currentPosition) !== JSON.stringify(defaultPosition)) {
                setDraggedPosition(defaultPosition)
            }
        },
        300,
        [currentPosition, defaultPosition]
    )

    useEffect(() => {
        if (currentPosition) {
            onPositionUpdate?.(currentPosition)
        }
    }, [currentPosition])

    useEffect(() => {
        if (sprite?.frameHeight && sprite?.frameWidth) {
            setDimensions({ height: sprite.frameHeight, width: sprite.frameWidth })
        }
    }, [sprite?.frameHeight, sprite?.frameWidth])

    return defaultPosition && sprite ? (
        <Layer
            {...layerProps}
            defaultPosition={defaultPosition}
            disabled={disabled || !text}
            ref={ref}
            sprite={sprite}
            onPositionUpdate={setCurrentPosition}
        />
    ) : null
}

export default forwardRef(ArtboardText)
